/**
 * The Sales & Growth Overview (WALLIYO-0132), built to the approved design.
 *
 * Activation, not acquisition. The funnel carries what was **lost** at each
 * step, because one that shows only survivors says the month went well however
 * many people gave up in it — and a pro who signs up and never quotes is a pro
 * Walliyo does not have.
 *
 * The list beside it is a call sheet: approved, in a trade somebody is asking
 * for, and never bid. Each row says why it is worth the call.
 */

import type { GrowthOverview as Data } from "@wallyo/contracts/console/overview";

import { t, type MessageId } from "../../i18n";
import { formatNumber } from "../../i18n/format";
import { QueueRows } from "./QueueRows";

export function GrowthOverviewView({ data }: { data: Data }) {
  return (
    <>
      <div className="ov-figures">
        {data.funnel.map((step) => (
          <div key={step.stepId} className="ov-figure">
            <span className="figure">{formatNumber(step.count)}</span>
            <span className="foot">{t(`overview.growth.step.${step.stepId}` as MessageId)}</span>
            {/* What it cost to get here. The step above is the good news; this
                is the half that tells somebody what to do today. */}
            {step.lost > 0 ? (
              <span className="foot danger ov-lost">
                −{formatNumber(step.lost)} {t(`overview.growth.lost.${step.stepId}` as MessageId)}
              </span>
            ) : null}
          </div>
        ))}
      </div>

      <div className="ov-split">
        <section className="card">
          <div className="ov-card-head">
            <h2>{t("overview.growth.stuck")}</h2>
            <span className="foot">{t("overview.growth.stuckWho")}</span>
          </div>
          <QueueRows rows={data.stuck} area="growth" empty={t("overview.growth.nobodyStuck")} />
        </section>

        <div className="ov-side">
          {data.promo ? (
            <section className="card">
              <h2>{data.promo.name}</h2>
              <p className="foot">
                {t("overview.growth.promoCost", { given: data.promo.given, earned: data.promo.earned })}
              </p>
              <p className="foot">{t("overview.growth.promoUsed", { used: formatNumber(data.promo.used) })}</p>
            </section>
          ) : null}

          {/* Standing advice, not a number: data is paid by the hour here, so an
              in-app notification is free to a pro and an SMS is not. */}
          <section className="card ov-callout hold">
            <h2>{t("overview.growth.beforeYouCampaign")}</h2>
            <p className="foot">{t("overview.growth.notificationsAreFree")}</p>
          </section>
        </div>
      </div>
    </>
  );
}
